import { request, requestEmpty } from './client'
import { asArray, asObject, num, optNum, optStr, optTime, str, text, time } from './parse'

/**
 * The agency's own Telegram channel, read as a source of objects.
 *
 * A year of adverts already exists there, with the photographs a client wants to
 * see. This is the queue between the channel and the inventory: a model reads
 * each post, and a person decides whether the reading is right before anything
 * becomes an object.
 *
 * The wire contract:
 *
 *   GET  /api/catalogue/posts?state=&limit=&offset=   listings.read
 *   POST /api/catalogue/sync                          listings.write
 *   POST /api/catalogue/posts/{id}/import             listings.write
 *   POST /api/catalogue/posts/{id}/skip               listings.write
 *
 * `parsed` is passed through as the server holds it: it is a model's reading and
 * every field of it may be missing, which is the point. A field that is absent
 * means the advert did not say it — never zero, never "no" (invariant 2).
 */

/** Where a post has got to. Not a listing status: a post is not an offer. */
export type PostState = 'pending' | 'parsed' | 'imported' | 'let' | 'announcement' | 'skipped'

/** What a model made of one advert. Every field is optional on purpose. */
export interface ParsedPost {
  readonly kind: 'apartment' | 'room' | 'bed' | undefined
  readonly title: string | undefined
  readonly summary: string | undefined
  readonly area: string | undefined
  readonly addressExact: string | undefined
  readonly areaSqm: number | undefined
  readonly floor: number | undefined
  readonly rooms: number | undefined
  readonly capacity: number | undefined
  readonly currency: string | undefined
  readonly rentMinor: number | undefined
  readonly billsIncluded: boolean | undefined
  readonly condominioMinor: number | undefined
  readonly depositMinor: number | undefined
  readonly depositMonths: number | undefined
  readonly depositNone: boolean
  readonly availableFrom: string | undefined
  readonly availableFromText: string | undefined
  readonly tenants: string | undefined
  /** The advert letting only to women or only to men. Empty is almost every flat. */
  readonly tenantGender: 'female' | 'male' | undefined
  readonly contractOffered: boolean | undefined
  readonly furnished: boolean | undefined
  readonly let: boolean
  readonly confidence: number
  unclear: readonly string[]
}

export interface CataloguePost {
  readonly id: string
  readonly link: string
  /** A photograph from the advert, so a person can see what it is. */
  readonly coverURL: string | undefined
  readonly state: PostState
  readonly note: string
  readonly body: string
  readonly publishedAt: string
  readonly editedAt: string | undefined
  readonly photos: number
  readonly parsed: ParsedPost | undefined
  readonly listingID: string | undefined
  readonly propertyID: string | undefined
}

export interface CataloguePage {
  readonly channel: string
  readonly posts: readonly CataloguePost[]
  readonly limit: number
  readonly offset: number
  readonly more: boolean
  /** How many posts sit in each state, so a tab can say how much is behind it.
   * A queue showing an empty "Ждут решения" with twenty-two unread posts one tab
   * away looks finished, and the person stops pressing sync. */
  readonly counts: Readonly<Record<string, number>>
}

export interface SyncResult {
  readonly posts: number
  readonly new: number
  readonly let: number
  readonly announcements: number
  readonly read: number
  /** Objects taken off the market because their own advert now says they are gone. */
  readonly closed: number
  /** Notes nobody could place, now somebody's task. */
  readonly handed: number
  /** Thumbnails fetched this round, and how many are still waiting. */
  readonly covers: number
  readonly coversLeft: number
  /** Posts nobody has read yet. One press reads a few; this is the rest. */
  readonly pendingLeft: number
  /** Which step gave up, when one did. Empty when all four finished. */
  readonly failed: string
}

export interface ImportResult {
  readonly listingID: string
  readonly propertyID: string
  readonly unitID: string
  readonly photos: number
}

/**
 * The reading is parsed leniently, unlike everything else in this directory.
 *
 * Elsewhere a missing field is a contract breach worth failing loudly over. Here
 * the whole document is a model's answer about an advert that may have said
 * nothing at all, so an absent field is the ordinary case and refusing it would
 * hide the queue behind an error the moment one post is vague.
 */
function parsedPost(value: unknown): ParsedPost | undefined {
  if (value === undefined || value === null) return undefined
  const source = asObject(value, 'parsed')

  const kind = optStr(source, 'Kind')
  return {
    kind: kind === 'apartment' || kind === 'room' || kind === 'bed' ? kind : undefined,
    title: optStr(source, 'Title'),
    summary: optStr(source, 'Summary'),
    area: optStr(source, 'Area'),
    addressExact: optStr(source, 'AddressExact'),
    areaSqm: optNum(source, 'AreaSqm'),
    floor: optNum(source, 'Floor'),
    rooms: optNum(source, 'Rooms'),
    capacity: optNum(source, 'Capacity'),
    currency: optStr(source, 'Currency'),
    rentMinor: optNum(source, 'RentMinor'),
    billsIncluded: typeof source.BillsIncluded === 'boolean' ? source.BillsIncluded : undefined,
    condominioMinor: optNum(source, 'CondominioMinor'),
    depositMinor: optNum(source, 'DepositMinor'),
    depositMonths: optNum(source, 'DepositMonths'),
    depositNone: source.DepositNone === true,
    availableFrom: optStr(source, 'AvailableFrom'),
    availableFromText: optStr(source, 'AvailableFromText'),
    tenants: optStr(source, 'Tenants'),
    tenantGender:
      source.TenantGender === 'female' || source.TenantGender === 'male'
        ? source.TenantGender
        : undefined,
    contractOffered: typeof source.ContractOffered === 'boolean' ? source.ContractOffered : undefined,
    furnished: typeof source.Furnished === 'boolean' ? source.Furnished : undefined,
    let: source.Let === true,
    confidence: typeof source.Confidence === 'number' ? source.Confidence : 0,
    unclear: Array.isArray(source.Unclear) ? source.Unclear.filter((u): u is string => typeof u === 'string') : [],
  }
}

function post(value: unknown): CataloguePost {
  const source = asObject(value, 'post')
  const state = str(source, 'state')
  return {
    id: str(source, 'id'),
    link: str(source, 'link'),
    coverURL: optStr(source, 'cover_url'),
    state: state as PostState,
    note: text(source, 'note'),
    body: text(source, 'body'),
    publishedAt: time(source, 'published_at'),
    editedAt: optTime(source, 'edited_at'),
    photos: num(source, 'photos'),
    parsed: parsedPost(source.parsed),
    listingID: optStr(source, 'listing_id'),
    propertyID: optStr(source, 'property_id'),
  }
}

function page(body: unknown): CataloguePage {
  const source = asObject(body, 'the channel')
  return {
    channel: str(source, 'channel'),
    posts: asArray(source.posts, 'posts').map(post),
    limit: num(source, 'limit'),
    offset: num(source, 'offset'),
    more: source.more === true,
    counts: counts(source.counts),
  }
}

/** The per-state tally. Absent counts as none rather than as an error: a page
 * without its numbers is still a page. */
function counts(value: unknown): Record<string, number> {
  if (value === undefined || value === null) return {}

  const source = asObject(value, 'counts')
  const out: Record<string, number> = {}
  for (const [state, posts] of Object.entries(source)) {
    if (typeof posts === 'number') out[state] = posts
  }
  return out
}

function syncResult(body: unknown): SyncResult {
  const source = asObject(body, 'the sync')
  return {
    posts: num(source, 'posts'),
    new: num(source, 'new'),
    let: num(source, 'let'),
    announcements: num(source, 'announcements'),
    read: num(source, 'read'),
    closed: num(source, 'closed'),
    handed: num(source, 'handed'),
    covers: num(source, 'covers'),
    coversLeft: num(source, 'covers_left'),
    pendingLeft: num(source, 'pending_left'),
    failed: text(source, 'failed'),
  }
}

function importResult(body: unknown): ImportResult {
  const source = asObject(body, 'the import')
  return {
    listingID: str(source, 'listing_id'),
    propertyID: str(source, 'property_id'),
    unitID: str(source, 'unit_id'),
    photos: num(source, 'photos'),
  }
}

export async function cataloguePosts(options: {
  state?: PostState
  limit?: number
  offset?: number
}): Promise<CataloguePage> {
  const query = new URLSearchParams()
  if (options.state) query.set('state', options.state)
  if (options.limit) query.set('limit', String(options.limit))
  if (options.offset) query.set('offset', String(options.offset))

  const suffix = query.toString()
  return request(`/api/catalogue/posts${suffix ? `?${suffix}` : ''}`, page)
}

export async function syncCatalogue(): Promise<SyncResult> {
  return request('/api/catalogue/sync', syncResult, { method: 'POST' })
}

export async function importPost(id: string): Promise<ImportResult> {
  return request(`/api/catalogue/posts/${id}/import`, importResult, { method: 'POST' })
}

export async function skipPost(id: string, reason: string): Promise<void> {
  return requestEmpty(`/api/catalogue/posts/${id}/skip`, { method: 'POST', body: { reason } })
}

/** A person's own reading of an advert. Every field is optional: sending one
 * changes one, and sending it empty clears it. */
export interface Correction {
  readonly kind?: string
  readonly title?: string
  readonly area?: string
  readonly address_exact?: string
  readonly rent?: number
  readonly bills_included?: boolean | null
  readonly deposit_amount?: number
  readonly deposit_months?: number
  readonly deposit_none?: boolean
  readonly available_from?: string
  readonly available_from_text?: string
  readonly capacity?: number
  readonly rooms?: number
  readonly area_sqm?: number
  readonly tenants?: string
  readonly tenant_gender?: string
  readonly summary?: string
}

/**
 * Corrects what we hold about one advert.
 *
 *   PATCH /api/catalogue/posts/{id}    listings.write
 *
 * The point of the whole screen: the model proposes and a person who can see the
 * advert and its photographs says what it actually offers. Without it a post the
 * model could not read was a dead end.
 */
export async function correctPost(id: string, fields: Correction): Promise<CataloguePost> {
  return request(`/api/catalogue/posts/${encodeURIComponent(id)}`, post, {
    method: 'PATCH',
    body: fields,
  })
}
